/**
 * The Adulthood Trap Grid.
 *
 * A grid of collection tiles with one "Cancel Adulthood" button hiding among
 * them. Chase the button and it dodges — swapping, warping across the grid, or
 * vanishing and rematerialising when cornered. You keep missing it and hitting
 * merchandise instead, which is the entire point.
 *
 * Design notes:
 * - Tiles never move in the DOM. Position comes from CSS `order`, and swaps are
 *   animated with FLIP (record rects -> reorder -> transform back -> release).
 *   Moving nodes would destroy focus and restart images.
 * - Works on touch: tapping the button's tile makes it dodge instead of
 *   navigating. The grid makes this viable because you aim at discrete cells
 *   rather than chasing a moving target with a hover you don't have.
 * - It is winnable. After `dodgeBudget` dodges it gives up and behaves as a
 *   normal link — otherwise it reads as broken rather than funny.
 */
class AdulthoodTrapGridComponent extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-trap-button]');
    /** @type {HTMLElement[]} */
    this.allTiles = Array.from(this.querySelectorAll('[data-trap-tile]'));
    if (!this.button || this.allTiles.length === 0) return;

    this.columns = Number(this.dataset.columns) || 4;
    this.cells = Number(this.dataset.cells) || 8;
    this.dodgeBudget = Number(this.dataset.dodgeBudget) || 8;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.dodges = 0;
    this.cooling = false;

    // Use a shuffled subset so repeat visits differ. Reduced motion keeps the
    // authored order — surprise reordering is disorienting.
    const pool = this.reduceMotion ? this.allTiles : shuffle([...this.allTiles]);
    this.tiles = pool.slice(0, this.cells - 1);
    for (const tile of this.tiles) tile.hidden = false;

    /** cell index -> element. Index 0..cells-1, laid out row-major. */
    this.occupants = [this.button, ...this.tiles];
    this.#applyOrder();

    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handleButtonActivate = this.handleButtonActivate.bind(this);

    if (!this.reduceMotion) {
      this.addEventListener('pointermove', this.handlePointerMove);
      // Covers touch and any click that lands before a dodge fires.
      this.button.addEventListener('click', this.handleButtonActivate);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.button?.removeEventListener('click', this.handleButtonActivate);
  }

  get caught() {
    return this.dodges >= this.dodgeBudget;
  }

  /** Writes each occupant's cell index onto its `order` style. */
  #applyOrder() {
    this.occupants.forEach((el, index) => {
      if (el) el.style.order = String(index);
    });
  }

  #buttonCell() {
    return this.occupants.indexOf(this.button);
  }

  /** The grid drops to 2 columns below 750px — read it rather than assume. */
  #activeColumns() {
    const template = getComputedStyle(this).gridTemplateColumns;
    const count = template.split(' ').filter(Boolean).length;
    return count > 0 ? count : this.columns;
  }

  /**
   * Ask the event what it is over rather than deriving a cell from geometry.
   *
   * An earlier version computed the hovered cell from the grid rect and the
   * column/row counts. That was wrong in practice: the button and the tiles do
   * not have identical heights, so rows drift and the computed cell rarely
   * matched the button's real one — it fired on roughly 1 chase in 12. Hit
   * testing via `event.target` is exact, O(1), and needs no rects at all.
   *
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (event.pointerType !== 'mouse' || this.caught || this.cooling) return;
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-trap-button]')) return;
    this.#dodge(this.#buttonCell());
  }

  /**
   * Touch (and any click that beats the pointermove) gets one dodge per tap
   * rather than navigating.
   *
   * @param {MouseEvent} event
   */
  handleButtonActivate(event) {
    if (this.caught) return; // Budget spent — let the link work.
    event.preventDefault();
    this.#dodge(this.#buttonCell());
  }

  /**
   * @param {number} avoidCell cell the pointer is in; never a valid target
   */
  #dodge(avoidCell) {
    const from = this.#buttonCell();
    if (from < 0) return;

    const cols = this.#activeColumns();
    const candidates = [];
    for (let i = 0; i < this.cells; i++) {
      if (i === from || i === avoidCell) continue;
      candidates.push(i);
    }
    if (candidates.length === 0) return;

    // Prefer somewhere not adjacent to the pointer, so it can't dodge straight
    // back under the cursor. If everything is adjacent it's cornered — that's
    // the "vanish" case.
    const distant = candidates.filter((i) => gridDistance(i, avoidCell, cols) > 1);
    const cornered = distant.length === 0;
    const pool = cornered ? candidates : distant;
    const to = pool[Math.floor(Math.random() * pool.length)];

    this.dodges += 1;
    this.cooling = true;
    this.dataset.dodging = 'true';

    if (cornered) this.#vanishTo(to, from);
    else this.#swap(from, to);

    // Without a cooldown the button ping-pongs under a moving cursor and reads
    // as broken rather than playful.
    window.setTimeout(() => {
      this.cooling = false;
      this.dataset.dodging = 'false';
      if (this.caught) this.#giveUp();
    }, 220);
  }

  /** Straight swap, FLIP-animated. */
  #swap(from, to) {
    const a = this.occupants[from];
    const b = this.occupants[to];
    const first = new Map([
      [a, a.getBoundingClientRect()],
      [b, b.getBoundingClientRect()],
    ]);

    this.occupants[from] = b;
    this.occupants[to] = a;
    this.#applyOrder();

    flip(first);
  }

  /** Fade out, reappear elsewhere — used when there is nowhere safe to slide. */
  #vanishTo(to, from) {
    this.button.dataset.vanishing = 'true';
    window.setTimeout(() => {
      const a = this.occupants[from];
      const b = this.occupants[to];
      this.occupants[from] = b;
      this.occupants[to] = a;
      this.#applyOrder();
      this.button.dataset.vanishing = 'false';
    }, 160);
  }

  /** Budget spent: stop dodging and read as catchable. */
  #giveUp() {
    this.button.dataset.caught = 'true';
    this.removeEventListener('pointermove', this.handlePointerMove);
  }
}

/**
 * FLIP: elements have already moved; transform them back to where they were,
 * then release so the CSS transition animates them into place.
 *
 * @param {Map<HTMLElement, DOMRect>} first
 */
function flip(first) {
  for (const [el, before] of first) {
    const after = el.getBoundingClientRect();
    const dx = before.left - after.left;
    const dy = before.top - after.top;
    if (dx === 0 && dy === 0) continue;

    el.style.transition = 'none';
    el.style.transform = `translate(${dx}px, ${dy}px)`;
    // Force a reflow so the browser paints the start position before we
    // animate away from it.
    void el.offsetWidth;
    el.style.transition = '';
    el.style.transform = '';
  }
}

/** Chebyshev distance between two cells, so diagonals count as adjacent. */
function gridDistance(a, b, cols) {
  if (b < 0) return Infinity;
  const ax = a % cols;
  const ay = Math.floor(a / cols);
  const bx = b % cols;
  const by = Math.floor(b / cols);
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/** @template T @param {T[]} list @returns {T[]} */
function shuffle(list) {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

if (!customElements.get('adulthood-trap-grid-component')) {
  customElements.define('adulthood-trap-grid-component', AdulthoodTrapGridComponent);
}
